// "Your turn", briefly, when the board comes back to you.
//
// The board turns, pieces move on their own and machines run between turns, so
// the moment control returns is easy to miss — especially in single player,
// where nothing else marks it. A caption that scales in and fades out is
// enough: it is over before it can become something to read.

import { useEffect, useState } from "react";

import { keyframes, REDUCED_MOTION, STILL, styled, useFreeArea } from "./kit";

/**
 * Scoped through the kit's `keyframes` rather than written as a `@keyframes`
 * key in `sx` — emotion scopes the name, and a bare `@keyframes` block silently
 * never applies.
 */
const announce = keyframes`
  0%   { opacity: 0; transform: scale(0.82); }
  18%  { opacity: 1; transform: scale(1.04); }
  30%  { transform: scale(1); }
  75%  { opacity: 1; }
  100% { opacity: 0; transform: scale(1.02); }
`;

const VISIBLE_MS = 1400;

/**
 * The free area, and inert.
 *
 * **Not the whole viewport** (designer, U9 review): a caption centred on the
 * window is centred on the chrome as well, and on a short screen the word
 * lands across the turn row. `useFreeArea()` is what the inset registry
 * already reports for exactly this — where something may be *placed* — so the
 * caption is centred in the board's own space rather than in the window's.
 *
 * `z.banner` is the rung a moment takes: over the board and over the pill,
 * under the pause menu and the modals, which are the surfaces that have asked
 * for the screen rather than merely spoken over it.
 */
const Stage = styled("div")(({ theme }) => ({
  position: "fixed",
  display: "grid",
  placeItems: "center",
  // The board underneath stays live: this is a caption, not a curtain.
  pointerEvents: "none",
  zIndex: theme.kit.z.banner
}));

/**
 * The word itself, in the heading face.
 *
 * The shadow is the ground token rather than a black: it sits over a lit
 * board, so what it needs is the page's own dark behind the letters.
 */
const Caption = styled("div")(({ theme }) => ({
  fontFamily: theme.kit.font.heading,
  fontWeight: 400,
  fontSize: "clamp(28px, 6vw, 64px)",
  letterSpacing: "0.02em",
  color: theme.kit.color.cream,
  textShadow: `0 2px 24px color-mix(in srgb, ${theme.kit.color.shadow} 70%, transparent)`,
  animation: `${announce} ${VISIBLE_MS}ms ease-out forwards`,
  [REDUCED_MOTION]: STILL
}));

export function TurnBanner({ label, turn }: { label: string; turn: number }) {
  // Keyed on the turn counter rather than on the label: two consecutive turns
  // of yours say the same thing, and re-running an animation needs the element
  // to be new rather than merely re-rendered.
  const [shown, setShown] = useState<number | null>(null);
  const free = useFreeArea();

  useEffect(() => {
    setShown(turn);
    const handle = window.setTimeout(() => setShown(null), VISIBLE_MS);
    return () => window.clearTimeout(handle);
  }, [turn]);

  if (shown === null) return null;

  return (
    <Stage
      key={shown}
      data-testid="turn-banner"
      // In `style`, never `sx`: it moves whenever a piece of chrome does, and
      // emotion mints a permanent class per distinct value.
      style={{ left: free.x, top: free.y, width: free.width, height: free.height }}
    >
      <Caption>{label}</Caption>
    </Stage>
  );
}
